import { DataLayer, DataLayerId } from './types';

// Key insertion order drives the layer-picker order. Two visible tiers,
// each alphabetised by `displayName`:
//
// 1. CTrees products (LAYER_PROVIDERS[id].primary === 'ctrees')
// 2. Data partners   (LAYER_PROVIDERS[id].primary !== 'ctrees')
//
// Tree Species Diversity is in the Data Partners tier even though CTrees
// packages the BIGMAP product — provenance attribution follows the
// upstream primary (USFS in that case), which is also how the picker's
// provider-mark cue reads. The `LayerPickerListbox` renders a small
// section header between the two tiers; drop a layer in the wrong
// alphabetical slot and the visual hierarchy breaks, so insertion order
// here is load-bearing.
//
// `resilience_priority` is intentionally last — it's hidden from the
// free-exploration picker and only activated by scenarios (see
// SCENARIO_ONLY_LAYERS in LayerPickerListbox.tsx).
export const DATA_LAYERS: Record<DataLayerId, DataLayer> = {
  // ── CTrees products (alphabetical by displayName) ────────────────────
  aboveground_biomass: {
    id: 'aboveground_biomass',
    displayName: 'Above Ground Biomass',
    description: 'Mean above-ground biomass density within the supply shed, reported annually from 2000 to 2025 at 100m resolution. Shows how living tree biomass has evolved over the past 25 years.',
    units: 'Mg/ha',
    colorScheme: 'sequential_green',
    legendType: 'continuous',
    chartTypes: ['line_chart', 'summary_table'],
    resolution: '100m',
    years: '2000-2025',
    status: 'live',
  },
  canopy_height: {
    id: 'canopy_height',
    displayName: 'Canopy Height',
    description: 'Tree canopy height rendered from an Earthscale-hosted raster. Provides vertical structure of the forest canopy across the supply shed. Coverage launching with California; other states to follow.',
    units: 'feet',
    colorScheme: 'sequential_green',
    legendType: 'continuous',
    chartTypes: ['histogram', 'summary_table'],
    resolution: '5m',
    years: '2024',
    status: 'live',
  },
  deforestation_alerts: {
    id: 'deforestation_alerts',
    displayName: 'Disturbance Alerts',
    description: 'Monthly forest disturbance area detected by LUCA (Land Use Change Alerts) satellite monitoring within the supply shed. Shows recent tree cover loss from Sentinel-2 imagery at 10m resolution. Without land-use attribution, detected loss is treated as disturbance rather than deforestation. More at ctrees.org/luca.',
    units: 'hectares',
    colorScheme: 'sequential_red',
    legendType: 'categorical',
    chartTypes: ['time_series_bar', 'summary_table'],
    resolution: '10m',
    years: '2018 – Present, monthly aggregation',
    status: 'live',
  },
  growth_curves: {
    id: 'growth_curves',
    displayName: 'Growth Projections',
    description: 'Projected forest growth-rate trajectories within the supply shed (2033-2132), with mean and 5th-95th percentile uncertainty band.',
    units: 'tons/ha/yr',
    colorScheme: 'sequential_green',
    legendType: 'continuous',
    chartTypes: ['line_chart'],
    resolution: '100m',
    years: '2033-2132 (projected)',
    status: 'live',
  },
  soil_carbon: {
    id: 'soil_carbon',
    displayName: 'Soil Carbon',
    description: 'Aggregated soil organic carbon stocks within the supply shed at 0-30 cm and 0-100 cm depth. Toggle depth to compare profiles.',
    units: 'tons C/acre',
    colorScheme: 'sequential_green',
    legendType: 'categorical',
    chartTypes: ['summary_table'],
    resolution: '100m',
    years: '2022',
    status: 'live',
  },
  tree_crown_segmentation: {
    id: 'tree_crown_segmentation',
    displayName: 'Tree-Level Attributes',
    description: 'Individual tree crown polygons from 60 cm NAIP imagery, each carrying biomass, volume, and diameter estimates derived from a stratified NSVB allometric coefficient raster. ~60M crowns across the Sierra Nevada supply shed; the left panel surfaces shed-wide totals and the map shows a coarse-grid total-tree-biomass density overlay. Coverage limited to the California Sierra Nevada 60-min shed.',
    units: 'Mg/ha',
    colorScheme: 'sequential_green',
    legendType: 'categorical',
    chartTypes: ['histogram', 'summary_table'],
    resolution: '60cm',
    years: '2020 (NAIP epoch)',
    status: 'live',
    statusNote: 'Pilot release — Sierra Nevada only.',
  },

  // ── Data partners (alphabetical by displayName) ──────────────────────
  forest_ownership: {
    id: 'forest_ownership',
    displayName: 'Forest Ownership',
    description: 'Forest ownership classified into seven types (Family, Corporate, Tribal, Federal, State, Local, Unknown) from the USFS RDS-2025-0045 raster (Harris, Caputo & Butler 2025). A single 2022 snapshot of who owns the forested land within the supply shed.',
    units: '% of forested acres',
    colorScheme: 'categorical',
    legendType: 'categorical',
    chartTypes: ['pie_chart', 'summary_table'],
    resolution: '10m',
    years: '2022',
    status: 'live',
  },
  imperiled_species: {
    id: 'imperiled_species',
    displayName: 'Imperiled Wildlife Species',
    description: 'NatureServe’s Map of Biodiversity Importance — Protection-Weighted Range-Size Rarity at 330m. Highlights unprotected places where small-range imperiled species (G1, G2, and ESA-listed) are most likely to occur, across vertebrates, plants, pollinators, and freshwater invertebrates.',
    units: 'PWRSR class (very low → very high)',
    colorScheme: 'plasma',
    legendType: 'categorical',
    chartTypes: ['histogram', 'summary_table'],
    resolution: '330m',
    years: '2024',
    status: 'live',
  },
  stand_density: {
    id: 'stand_density',
    displayName: 'Stand Density',
    description: 'Relative density (RD) at 30m resolution — the ratio of current stand density to the biological maximum (Reineke 1933, Long 1985). Values run 0 (open) → 1 (overstocked). Three snapshots (2016, 2020, 2022) show whether the shed is densifying.',
    units: 'RD (0–1)',
    colorScheme: 'rdylgn_4class',
    legendType: 'categorical',
    chartTypes: ['histogram', 'line_chart', 'summary_table'],
    resolution: '30m',
    years: '2016, 2020, 2022',
    status: 'live',
  },
  species_diversity: {
    id: 'species_diversity',
    displayName: 'Tree Species Diversity',
    description: 'Tree species composition and diversity within the supply shed, mapped at 30m resolution across 327 species. Measures each species\' share of total above-ground biomass to assess ecosystem diversity and resilience.',
    units: '% of biomass',
    colorScheme: 'categorical',
    legendType: 'categorical',
    chartTypes: ['pie_chart', 'summary_table'],
    resolution: '30m',
    years: '2018 (2014-2018 epoch)',
    status: 'live',
  },
  fire_risk: {
    id: 'fire_risk',
    displayName: 'Wildfire Hazard Potential',
    description: 'Five-class wildfire hazard (very low → very high) from the USFS Wildfire Risk to Communities 30m product (Scott et al. 2024). Combines FlamMap-modeled fire behavior, fuels, and climate drivers into a single hazard rating for the supply shed.',
    units: 'class (very low → very high)',
    colorScheme: 'sequential_red',
    legendType: 'categorical',
    chartTypes: ['summary_table'],
    resolution: '30m',
    years: '2022',
    status: 'live',
  },

  // ── Scenario-only (hidden from picker; activated by Carbon Defense) ──
  resilience_priority: {
    id: 'resilience_priority',
    displayName: 'Resilience Priority',
    description: 'Synthesis layer for the Carbon Defense scenario — a 5-tier priority spectrum painted on private timberland. Each pixel is assigned a tier via a documented WHP × RD decision matrix (lib/resiliencePriorityColors.ts): tier 5 (red) covers high/very-high fire-risk stands that are also dense or overstocked; tier 1 (green) covers low-risk open stands. Colors match the WHP RdYlGn ramp so the spectrum reads as a fire-risk gradient on private acres. The matrix insight card surfaces the underlying WHP × RD cells.',
    units: 'priority tier (1–5)',
    colorScheme: 'rdylgn_5class',
    legendType: 'categorical',
    chartTypes: ['summary_table'],
    resolution: '30m',
    years: 'WHP 2022 × RD 2022 × Ownership 2022',
    status: 'live',
  },
};
